import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { useGameStore } from '@/lib/stores/gameStore';
import { CONNECTION_STATUS_TIMEOUT_MS } from '@danda/shared';

interface ConnectionStatusProps {
  isCollapsed?: boolean;
}

/**
 * Connection Status Indicator - "Streetlight" style per UX spec
 *
 * Per spec (lines 1280-1310):
 * - Circular indicator (●) with color states
 * - Green (connected), Orange (connecting), Red (offline)
 * - Text showing latency or status
 * - Also displays last LLM response latency for diagnostics
 *
 * Route-aware:
 * - On /play routes: shows game server connection status
 * - On other routes: shows lobby server connection status
 */
export function ConnectionStatus({ isCollapsed = false }: ConnectionStatusProps) {
  const location = useLocation();
  const isGameRoute = location.pathname.startsWith('/play');
  const isLobbyRoute = location.pathname.startsWith('/lobby');

  // All hooks must be called unconditionally before any early returns
  const {
    isOnline: lobbyOnline,
    connectionLatency,
    ttftLatency,
    totalLlmLatency,
    isWaitingForResponse,
  } = useLobbyStore();

  const { connectionStatus } = useGameStore();
  const gameConnected = connectionStatus.status === 'connected';

  const isOnline = isGameRoute ? gameConnected : lobbyOnline;
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [reconnectStatus, setReconnectStatus] = React.useState<'idle' | 'reconnecting' | 'failed'>(
    'idle'
  );

  // Detect connecting state (when transitioning from offline to online)
  React.useEffect(() => {
    if (!isOnline) {
      setIsConnecting(true);
      if (reconnectStatus === 'idle') {
        setReconnectStatus('reconnecting');
        const timeout = setTimeout(() => {
          // Use functional setter to check current state at fire-time, not stale closure value
          setReconnectStatus((current) => (current === 'reconnecting' ? 'failed' : current));
        }, 10000);
        return () => clearTimeout(timeout);
      }
      // Reset connecting state after a delay if still offline
      const timeout = setTimeout(() => setIsConnecting(false), CONNECTION_STATUS_TIMEOUT_MS);
      return () => clearTimeout(timeout);
    } else {
      setIsConnecting(false);
      if (reconnectStatus !== 'idle') {
        setReconnectStatus('idle');
      }
    }
  }, [isOnline, reconnectStatus]);

  const getStatusColor = () => {
    if (isOnline) return 'text-green-500'; // Connected
    if (reconnectStatus === 'failed') return 'text-destructive';
    if (isConnecting || reconnectStatus === 'reconnecting') return 'text-orange-500'; // Connecting
    return 'text-destructive'; // Offline
  };

  const getStatusText = () => {
    if (isOnline) {
      // Show ping latency if available, otherwise nothing (green dot is enough)
      return connectionLatency ? `${connectionLatency}ms` : '';
    }
    if (reconnectStatus === 'failed') {
      return 'Connection Failed';
    }
    if (isConnecting || reconnectStatus === 'reconnecting') {
      return 'Reconnecting...'; // Per spec line 1286
    }
    return 'Offline';
  };

  // Format LLM latency in seconds for better readability
  const formatLatency = (ms: number) => {
    if (ms >= 1000) {
      return `${(ms / 1000).toFixed(1)}s`;
    }
    return `${ms}ms`;
  };

  // No WS connection on dashboard — don't show a misleading reconnecting state
  if (!isGameRoute && !isLobbyRoute) return null;

  // Same layout as nav buttons: h-9, w-full, justify-start/center based on collapsed
  const statusText = getStatusText() || 'Connected';
  const tooltipText = `${statusText}${ttftLatency ? ` | TTFT: ${formatLatency(ttftLatency)}` : ''}${totalLlmLatency ? ` | LLM: ${formatLatency(totalLlmLatency)}` : ''}`;

  return (
    <div
      className={`flex items-center w-full h-9 px-4 ${isCollapsed ? 'justify-center px-2' : ''}`}
      title={isCollapsed ? tooltipText : undefined}
    >
      <span
        className={`${getStatusColor()} text-base leading-none shrink-0 ${!isCollapsed ? 'mr-3' : ''} ${isWaitingForResponse ? 'animate-pulse' : ''}`}
        aria-hidden="true"
      >
        ●
      </span>
      {!isCollapsed && (
        <div className="flex flex-col min-w-0 text-sm">
          <span className="text-sidebar-foreground">{statusText}</span>
          {(ttftLatency || totalLlmLatency) && (
            <span className="text-[10px] text-muted-foreground">
              {`TTFT: ${isWaitingForResponse ? '...' : ttftLatency ? formatLatency(ttftLatency) : '?'} · LLM: ${isWaitingForResponse ? '...' : totalLlmLatency ? formatLatency(totalLlmLatency) : '?'}`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
